import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, ArrowUpDown } from 'lucide-react';
import { Booking } from '@shared/schema';

export interface BookingWithRelations extends Booking {
    clientName?: string;
    advertiserName?: string;
    cycleName?: string;
    retailerName?: string;
}

export interface BookingColumnHandlers {
    onEdit: (booking: BookingWithRelations) => void;
    onDelete: (id: number) => void;
}

const getStatusBadge = (status: string) => {
    switch (status) {
        case 'pipeline':
            return <Badge variant="secondary">Pipeline</Badge>;
        case 'optioned':
            return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200">Optioned</Badge>;
        case 'booked':
            return <Badge className="bg-green-100 text-green-800 hover:bg-green-200">Booked</Badge>;
        case 'cancelled':
            return <Badge variant="destructive">Cancelled</Badge>;
        default:
            return <Badge variant="outline">{status}</Badge>;
    }
};

export function createBookingColumns(
    handlers: BookingColumnHandlers
): ColumnDef<BookingWithRelations>[] {
    const { onEdit, onDelete } = handlers;

    return [
        {
            accessorKey: 'name',
            header: ({ column }) => {
                return (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                        className="h-8 px-2"
                    >
                        Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                );
            },
            cell: ({ row }) => (
                <span className="font-medium">{row.original.name}</span>
            ),
        },
        {
            accessorKey: 'clientName',
            header: ({ column }) => {
                return (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                        className="h-8 px-2"
                    >
                        Client
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                );
            },
            cell: ({ row }) => (
                <span className="text-sm text-muted-foreground">{row.original.clientName || '—'}</span>
            ),
        },
        {
            accessorKey: 'advertiserName',
            header: ({ column }) => {
                return (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                        className="h-8 px-2"
                    >
                        Advertiser
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                );
            },
            cell: ({ row }) => (
                <span className="text-sm">{row.original.advertiserName || '—'}</span>
            ),
        },
        {
            accessorKey: 'cycleName',
            header: ({ column }) => {
                return (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                        className="h-8 px-2"
                    >
                        Cycle
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                );
            },
            cell: ({ row }) => (
                <span className="text-sm text-muted-foreground">{row.original.cycleName || '—'}</span>
            ),
        },
        {
            accessorKey: 'status',
            header: ({ column }) => {
                return (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                        className="h-8 px-2"
                    >
                        Status
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                );
            },
            cell: ({ row }) => getStatusBadge(row.original.status || 'pipeline'),
        },
        {
            accessorKey: 'bookingValue',
            header: ({ column }) => {
                return (
                    <div className="flex justify-end">
                        <Button
                            variant="ghost"
                            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                            className="h-8 px-2"
                        >
                            Value
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                );
            },
            cell: ({ row }) => {
                const value = row.original.bookingValue;
                const currency = row.original.currency || 'EUR';
                const formatted = new Intl.NumberFormat('en-IE', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: 0,
                }).format(value || 0);
                return <div className="text-right text-sm">{value ? formatted : '—'}</div>;
            },
        },
        {
            id: 'actions',
            header: () => <div className="text-right">Actions</div>,
            cell: ({ row }) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(row.original as any)}
                        title="Edit booking"
                    >
                        <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(row.original.id)}
                        title="Cancel booking"
                    >
                        <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                </div>
            ),
            enableSorting: false,
        },
    ];
}

export function createReadOnlyBookingColumns(): ColumnDef<BookingWithRelations>[] {
    // Reuse columns except the last one impactfully (actions).
    // Actually, I'll copy-paste or abstract. Copy-paste is safer for now to avoid breaking existing.
    // Wait, I can reuse the logic.
    const fullCols = createBookingColumns({ onEdit: () => { }, onDelete: () => { } });
    return fullCols.slice(0, -1); // Remove Actions column
}

export function createConversionBookingColumns(
    onConvert: (booking: BookingWithRelations) => void
): ColumnDef<BookingWithRelations>[] {
    const baseCols = createReadOnlyBookingColumns();

    return [
        ...baseCols,
        {
            id: "actions",
            cell: ({ row }) => {
                const booking = row.original;
                // Check if eligible for conversion (Booked status only, not yet converted)
                const isConvertible = booking.status === 'booked' && !booking.convertedAt;
                const isConverted = !!booking.convertedAt;

                if (isConverted) {
                    return <span className="text-xs text-muted-foreground italic">Converted</span>;
                }

                if (!isConvertible) return null;

                return (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
                        onClick={() => onConvert(booking)}
                    >
                        Convert
                    </Button>
                );
            },
        },
    ];
}
